import { AxiosGlobal } from "../shared/axios";
import { AxiosPromise } from "axios";
import configs from "../../configs";
import { store } from "../../store/store";

/**
 * API Gateway Admin endpoints.
 * 
 * These use X-Admin-Token authentication (not Bearer).
 * The admin token is stored in configs.apiGatewayAdminToken
 * or can be set in localStorage as 'api_gateway_admin_token'.
 */
export class ApiGatewayLiveApi extends AxiosGlobal {

    private getAdminToken(): string {
        // Try config first, then localStorage fallback
        return (configs as any).apiGatewayAdminToken
            || localStorage.getItem('api_gateway_admin_token')
            || '';
    }

    private getCronSecret(): string {
        return (configs as any).apiGatewayCronSecret
            || localStorage.getItem('api_gateway_cron_secret')
            || '';
    }

    private adminHeaders() {
        return {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Admin-Token": this.getAdminToken(),
            "Access-Control-Allow-Origin": "*"
        };
    }

    private cronHeaders() {
        return {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Cron-Secret": this.getCronSecret(),
            "Access-Control-Allow-Origin": "*"
        };
    }

    // ==================== HEALTH & SYSTEM ====================

    getHealth(): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/v1/health`);
    }

    getSystemHealth(): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/v1/admin/system/health`, {
            headers: this.adminHeaders(),
        });
    }

    getSystemStats(): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/v1/admin/system/stats`, {
            headers: this.adminHeaders(),
        });
    }

    // ==================== PARTNER MANAGEMENT ====================

    getAllPartners(page: number, perPage: number, query: any): AxiosPromise<any> {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('per_page', perPage.toString());
        if (query?.search) params.append('search', query.search);
        if (query?.status) params.append('status', query.status);

        return this.axios.get(`${configs.context}/v1/admin/partners?${params.toString()}`, {
            headers: this.adminHeaders(),
        });
    }

    getSinglePartner(id: number): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/v1/admin/partners/${id}`, {
            headers: this.adminHeaders(),
        });
    }


    // ==================== PARTNER BILLING ====================

    // Rate Agreements
    getPartnerRateAgreements(partnerId: any): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/v1/admin/partners/${partnerId}/rate-agreements`, {
            headers: this.adminHeaders(),
        });
    }

    createPartnerRateAgreement(partnerId: any, data: any): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/v1/admin/partners/${partnerId}/rate-agreements`, data, {
            headers: this.adminHeaders(),
        });
    }

    updateRateAgreement(agreementId: any, data: any): AxiosPromise<any> {
        return this.axios.put(`${configs.context}/v1/admin/rate-agreements/${agreementId}`, data, {
            headers: this.adminHeaders(),
        });
    }

    // Invoices
    getAllInvoices(page: number, query: any): AxiosPromise<any> {
        const params = new URLSearchParams();
        params.append('page', String(page));
        if (query?.status) params.append('status', query.status);
        if (query?.partner_id) params.append('partner_id', query.partner_id);
        return this.axios.get(`${configs.context}/v1/admin/invoices?${params.toString()}`, {
            headers: this.adminHeaders(),
        });
    }

    getPartnerInvoices(partnerId: any, status?: string): AxiosPromise<any> {
        const query = status ? `?status=${status}` : '';
        return this.axios.get(`${configs.context}/v1/admin/partners/${partnerId}/invoices${query}`, {
            headers: this.adminHeaders(),
        });
    }

    getInvoiceDetail(invoiceId: any): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/v1/admin/invoices/${invoiceId}`, {
            headers: this.adminHeaders(),
        });
    }

    generatePartnerInvoice(partnerId: any, data: any): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/v1/admin/partners/${partnerId}/invoices/generate`, data, {
            headers: this.adminHeaders(),
        });
    }

    deleteRateAgreement(agreementId: any): AxiosPromise<any> {
        return this.axios.delete(`${configs.context}/v1/admin/rate-agreements/${agreementId}`, {
            headers: this.adminHeaders(),
        });
    }

    updateInvoiceStatus(invoiceId: any, data: any): AxiosPromise<any> {
        return this.axios.put(`${configs.context}/v1/admin/invoices/${invoiceId}/status`, data, {
            headers: this.adminHeaders(),
        });
    }

    createPartner(data: any): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/v1/admin/partners`, data, {
            headers: this.adminHeaders(),
        });
    }

    updatePartner(id: number, data: any): AxiosPromise<any> {
        return this.axios.put(`${configs.context}/v1/admin/partners/${id}`, data, {
            headers: this.adminHeaders(),
        });
    }

    approvePartner(id: number, data?: any): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/v1/admin/partners/${id}/approve`, data || { generate_api_key: true }, {
            headers: this.adminHeaders(),
        });
    }

    rejectPartner(id: number, data: any): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/v1/admin/partners/${id}/reject`, data, {
            headers: this.adminHeaders(),
        });
    }

    suspendPartner(id: number, data: any): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/v1/admin/partners/${id}/suspend`, data, {
            headers: this.adminHeaders(),
        });
    }

    reactivatePartner(id: number): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/v1/admin/partners/${id}/reactivate`, {}, {
            headers: this.adminHeaders(),
        });
    }

    creditWallet(id: number, data: any): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/v1/admin/partners/${id}/wallet/credit`, data, {
            headers: this.adminHeaders(),
        });
    }

    debitWallet(id: number, data: any): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/v1/admin/partners/${id}/wallet/debit`, data, {
            headers: this.adminHeaders(),
        });
    }

    // ==================== API KEY MANAGEMENT ====================

    getAllApiKeys(page: number, perPage: number, query: any): AxiosPromise<any> {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('per_page', perPage.toString());
        if (query?.type) params.append('type', query.type);
        if (query?.is_active !== undefined) params.append('is_active', query.is_active);
        if (query?.search) params.append('search', query.search);

        return this.axios.get(`${configs.context}/v1/admin/api-keys?${params.toString()}`, {
            headers: this.adminHeaders(),
        });
    }

    getSingleApiKey(id: number): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/v1/admin/api-keys/${id}`, {
            headers: this.adminHeaders(),
        });
    }

    createApiKey(data: any): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/v1/admin/api-keys`, data, {
            headers: this.adminHeaders(),
        });
    }

    revokeApiKey(id: number): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/v1/admin/api-keys/${id}/revoke`, {}, {
            headers: this.adminHeaders(),
        });
    }

    rotateApiKey(id: number): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/v1/admin/api-keys/${id}/rotate`, {}, {
            headers: this.adminHeaders(),
        });
    }

    approveCustomerAccess(userId: number): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/v1/admin/customer-api-access/${userId}/approve`, {}, {
            headers: this.adminHeaders(),
        });
    }

    rejectCustomerAccess(userId: number): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/v1/admin/customer-api-access/${userId}/reject`, {}, {
            headers: this.adminHeaders(),
        });
    }

    // ==================== ANALYTICS ====================

    getAnalyticsOverview(period: number): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/v1/admin/analytics/overview?period=${period}`, {
            headers: this.adminHeaders(),
        });
    }

    getAnalyticsRequests(period: number, source?: string): AxiosPromise<any> {
        const params = new URLSearchParams();
        params.append('period', period.toString());
        if (source) params.append('source', source);

        return this.axios.get(`${configs.context}/v1/admin/analytics/requests?${params.toString()}`, {
            headers: this.adminHeaders(),
        });
    }

    getTopConsumers(period: number, limit: number): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/v1/admin/analytics/top-consumers?period=${period}&limit=${limit}`, {
            headers: this.adminHeaders(),
        });
    }

    getRecentErrors(hours: number, limit: number): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/v1/admin/analytics/errors?hours=${hours}&limit=${limit}`, {
            headers: this.adminHeaders(),
        });
    }

    // ==================== CRON TASKS ====================

    runCronTask(task: string): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/v1/cron/${task}`, {
            headers: this.cronHeaders(),
        });
    }

    runAllCronTasks(): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/v1/cron/run-all`, {
            headers: this.cronHeaders(),
        });
    }

    // ==================== PARTNER MARKUP ====================

    setPartnerMarkup(partnerId: number, data: any): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/v1/admin/partners/${partnerId}/markup`, data, {
            headers: this.adminHeaders(),
        });
    }

    // ==================== PORTAL ACCOUNT MANAGEMENT ====================

    getAllPortalAccounts(page: number, perPage: number, query: any): AxiosPromise<any> {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('per_page', perPage.toString());
        if (query?.search) params.append('search', query.search);
        if (query?.status) params.append('status', query.status);
        if (query?.account_type) params.append('account_type', query.account_type);

        return this.axios.get(`${configs.context}/v1/admin/portal-accounts?${params.toString()}`, {
            headers: this.adminHeaders(),
        });
    }

    getSinglePortalAccount(id: number): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/v1/admin/portal-accounts/${id}`, {
            headers: this.adminHeaders(),
        });
    }

    approvePortalAccount(id: number, data?: any): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/v1/admin/portal-accounts/${id}/approve`, data || {}, {
            headers: this.adminHeaders(),
        });
    }

    rejectPortalAccount(id: number, data: any): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/v1/admin/portal-accounts/${id}/reject`, data, {
            headers: this.adminHeaders(),
        });
    }

    deletePortalAccount(id: number): AxiosPromise<any> {
        return this.axios.delete(`${configs.context}/v1/admin/portal-accounts/${id}`, {
            headers: this.adminHeaders(),
        });
    }
}